import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ShieldAlert, LogOut, RefreshCw } from "lucide-react";
import { UserProfile } from "../../types";
import { signOut } from "../../services/auth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  user: UserProfile | null;
  loading: boolean;
  requiredRole?: "admin" | "user" | "lead" | "contributor" | "viewer";
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  user,
  loading,
  requiredRole,
}) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#2ba5b5] border-t-transparent" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Loading MLC Proposal HQ...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Account suspended check
  if (user.status === "suspended") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center dark:bg-slate-950">
        <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-8 shadow-lg dark:border-rose-900/60 dark:bg-slate-900">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-950/60">
            <ShieldAlert className="h-7 w-7 text-rose-600 dark:text-rose-400" />
          </div>
          <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
            Account Suspended
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Your account ({user.email}) has been suspended. Please contact an administrator to
            reactivate your access.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => signOut()}
              className="btn-animate inline-flex items-center gap-2 rounded-lg bg-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-300"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Access Not Granted check (User from Staff HQ who is not involved in RFPs)
  if (user.status !== "active") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center dark:bg-slate-950">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <img
            src="/mlc-logo.png"
            alt="Multilingual Connections"
            className="mx-auto h-12 w-12 object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = "/favicon.png";
            }}
          />
          <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
            Proposal HQ Access Required
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Hello{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              {user.displayName}
            </span>{" "}
            (<span className="font-mono text-xs">{user.email}</span>).
          </p>
          <p className="mt-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
            Your MLC Google account is verified, but your profile has not been granted access to the
            RFP Response Workspace yet. Only staff members involved in the proposal process have
            active access.
          </p>
          <div className="mt-4 rounded-lg bg-teal-50/70 p-3 text-xs text-[#238997] dark:bg-teal-950/40 dark:text-teal-300">
            Please contact a Proposal HQ Administrator to request access and have your role
            assigned.
          </div>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span>Check Access Again</span>
            </button>
            <button
              type="button"
              onClick={() => signOut()}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white hover:bg-[#238997]"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requiredRole === "admin" && user.role !== "admin") {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-6 text-center">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Access Restricted</h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          You need administrator privileges to view this section.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};
